import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconModule } from '@angular/material/icon'
import { MatTabsModule } from '@angular/material/tabs'
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router'

import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, map, tap } from 'rxjs'

import { UserSectionAccessComponent } from '~/core/components'
import { DestroyDirective } from '~/core/directives'
import { CompanyFacade, ForecastListFacade, RouterFacade, UserFacade } from '~/core/features'
import { EGuardEntityName, EGuardSection, EGuardSectionName } from '~/core/models'

import { CompanyHeaderCardComponent } from './components/company-header-card/company-header-card.component'
import { ForecastNotFoundComponent } from './components/forecast-not-found/forecast-not-found.component'
import { TAB_EDGAR_REPORT_LINK, TAB_FULL_EDGAR_REPORT_LINK, TAB_LINKS } from './constants'

@Component({
  selector: 'app-company-info',
  templateUrl: './company-info.component.html',
  styleUrl: './company-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  imports: [
    AsyncPipe,
    RouterLink,
    RouterOutlet,
    MatIconModule,
    MatTabsModule,
    TranslateModule,
    RouterLinkActive,
    ForecastNotFoundComponent,
    CompanyHeaderCardComponent,
    UserSectionAccessComponent,
  ],
})
export class CompanyInfoComponent implements OnInit {
  private readonly forecastListFacade = inject(ForecastListFacade)
  private readonly destroyRef = inject(DestroyRef)
  private readonly activatedRoute = inject(ActivatedRoute)

  readonly routerFacade = inject(RouterFacade)

  readonly companyFacade = inject(CompanyFacade)

  readonly userFacade = inject(UserFacade)

  readonly guardSection = signal<EGuardSection | null | undefined>(undefined)
  readonly inRequest$ = new BehaviorSubject<Boolean>(false)

  TAB_LINKS = TAB_LINKS
  TAB_EDGAR_REPORT_LINK = TAB_EDGAR_REPORT_LINK
  TAB_FULL_EDGAR_REPORT_LINK = TAB_FULL_EDGAR_REPORT_LINK

  EGuardSection = EGuardSection
  EGuardEntityName = EGuardEntityName
  EGuardSectionName = EGuardSectionName

  constructor() {}

  ngOnInit(): void {
    this.routerFacade.isCompanyPage$
      .pipe(
        tap(isCompanyPage => {
          this.guardSection.set(
            isCompanyPage ? EGuardSection.COMPANY_INFO : EGuardSection.FORECAST_INFO
          )
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()

    combineLatest([this.forecastListFacade.inRequest$, this.companyFacade.inRequest$])
      .pipe(
        map(([inRequestForecast, inRequestCompany]) => inRequestForecast || inRequestCompany),
        tap(inRequest => this.inRequest$.next(inRequest)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }
}
